import React, { useEffect, useRef, useState } from 'react';
import { getOrElse, getOrUndefined } from 'util/resource';
import { emptyPaginatedResult } from 'util/paginationUtils';
import { useHistory, useLocation } from 'react-router-dom';
import { useCommunities } from 'apis/CompanyAPI/communities/useCommunities';
import { ICompany } from 'types/company';
import { CompanyViewAs } from 'domain/companies/roleUtils';
import { useCompanyPulse } from 'apis/CompanyAPI/companies/useCompanyPulse';
import ActivityFeed from './Updates';
import PostUpdateDialog from 'domain/Updates/company/PostUpdateDialog';
import PopMenu from 'ui/modules/PopMenu';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import { BODY_ID } from 'ui/views/layouts/BaseLayout/ScrollableBody';
import { useCompanyChecklistSessionStorage } from 'apis/OnboardingAPI';
import { companyUpdateAPIurls } from 'apis/CompanyAPI/content/UpdateAPI';
import { CommunityUpdate, CompanyUpdate } from 'types/content';
import Update from 'pages/Community/Overview/CommunityUpdates/Update';
import { CompanyOverviewSection } from 'urls';
import PostUpdateField from 'domain/Updates/shared/PostUpdateField';
import { useSelfUserProfile } from 'apis/CompanyAPI/users/useSelfUserProfile';
import { asUserAuthor } from 'domain/shared/author';
import usePaginatedResource from 'util/resource/usePaginatedResource';
import Resource from 'util/resource/Resource';

export const COMPANY_INITIAL_PAGINATION = { page: 1, limit: 5 };

export default function CompanyOverviewActivitySection({
  company,
  viewAs,
  onChangeSection,
}: {
  company: ICompany;
  viewAs: CompanyViewAs;
  onChangeSection: (section: CompanyOverviewSection) => string | (() => void);
}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isWriteUpdateInUrl = params.get('writeUpdate') === '1';

  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);

  function removeUrlParam(param: string) {
    if (queryParams.has(param)) {
      queryParams.delete(param);
      history.replace({
        search: queryParams.toString(),
      });
    }
  }

  const [isPostingUpdate, setIsPostingUpdate] = useState(isWriteUpdateInUrl);

  // Update the dialog state when URL changes
  useEffect(() => {
    setIsPostingUpdate(isWriteUpdateInUrl);
  }, [isWriteUpdateInUrl]);

  const onClose = () => {
    setIsPostingUpdate(false);
    // To make the checklist on the company page open the update dialog after closing it, we need to reset the url
    removeUrlParam('writeUpdate');
  };

  const [shouldShowArchivedUpdates, setShouldShowArchivedUpdates] = useState(false);

  const [updatesResource, reloadUpdates, onSeeMoreUpdates] = usePaginatedResource<CompanyUpdate>(
    paginationOptions => companyUpdateAPIurls.list(company.id, shouldShowArchivedUpdates, paginationOptions),
    COMPANY_INITIAL_PAGINATION,
  );

  // If update is posted to a community we show it on the company feed right away
  const [updatePostedInCommunity, setUpdatePostedInCommunity] = useState<CommunityUpdate>();

  const { resource: pulseResource } = useCompanyPulse(company.slug);
  const communitiesResource = useCommunities();
  const communities = getOrUndefined(communitiesResource.resource)?.values ?? [];

  const isCompanyAdmin = viewAs === 'Admin';
  const canPostUpdate = isCompanyAdmin;

  const updatesRef = useRef<HTMLDivElement>(null);

  const scrollToUpdatesSection = () => {
    if (updatesRef.current) {
      const updatesPosition = updatesRef.current.offsetTop;
      const body = document.getElementById(BODY_ID);
      body?.scrollTo({ top: updatesPosition - 60, behavior: 'smooth' });
    }
  };
  const actitivies = getOrElse(pulseResource, emptyPaginatedResult()).values;

  const { markStepAsCompleted } = useCompanyChecklistSessionStorage(company.slug);

  const community = communities.find(c => c.id === updatePostedInCommunity?.communityId)?.details;

  const { resource: userProfileResource } = useSelfUserProfile();
  const userProfile = getOrUndefined(userProfileResource);
  const author = asUserAuthor(userProfile);

  return (
    <>
      <Resource resource={updatesResource}>
        {updates => {
          return (
            <div style={{ maxWidth: 820 }}>
              {canPostUpdate && (
                <div data-intercom-target="company-post-updates">
                  <PostUpdateDialog
                    isOpen={isPostingUpdate}
                    onClose={onClose}
                    company={company}
                    onUpdatePosted={update => {
                      // Only set if it's a community update
                      if (update && 'communityId' in update) {
                        setUpdatePostedInCommunity(update as CommunityUpdate);
                      }
                      scrollToUpdatesSection();
                      markStepAsCompleted('PostFirstUpdate');
                      reloadUpdates();
                      setIsPostingUpdate(false);
                    }}
                  />
                  <PostUpdateField author={author} onClick={() => setIsPostingUpdate(true)} />
                </div>
              )}
              {isCompanyAdmin && (
                <div className="u-flex-end u-half-spacing-y">
                  <PopMenu
                    items={[
                      {
                        text: <>Latest</>,
                        onClick: () => setShouldShowArchivedUpdates(false),
                        selected: !shouldShowArchivedUpdates,
                      },
                      {
                        text: <>Archive</>,
                        onClick: () => setShouldShowArchivedUpdates(true),
                        selected: shouldShowArchivedUpdates,
                      },
                    ]}
                    anchorOrigin={{
                      horizontal: 'left',
                      vertical: 'bottom',
                    }}
                    renderAnchor={onClick => (
                      <button onClick={onClick} className="text-small" style={{ fontWeight: 500 }}>
                        {shouldShowArchivedUpdates === true ? 'Archived' : 'Latest'}
                        <ChevronDownIcon className="u-quarter-spacing-left" fontSize="inherit" />
                      </button>
                    )}
                  />
                </div>
              )}
              <div ref={updatesRef}>
                {updatePostedInCommunity && community && (
                  <Update
                    className="u-half-spacing-bottom"
                    update={updatePostedInCommunity}
                    creator={updatePostedInCommunity.update.createdBy}
                    community={community}
                    postedBy={{ type: 'company', company }}
                    shownIn="company"
                    onArchive={() => reloadUpdates()}
                    onDelete={reloadUpdates}
                    onEdit={reloadUpdates}
                    canEditPost={false}
                  />
                )}
                <ActivityFeed
                  shouldShowArchivedUpdates={shouldShowArchivedUpdates}
                  company={company}
                  updates={updates}
                  activities={actitivies}
                  viewAs={viewAs}
                  onSeeMore={onSeeMoreUpdates}
                  reloadUpdates={reloadUpdates}
                  onChangeSection={onChangeSection}
                  isPaginating={updatesResource.state === 'fetched' && updatesResource.isValidating}
                />
              </div>
            </div>
          );
        }}
      </Resource>
    </>
  );
}
